import axios from "axios";

import { tokenManager } from "../utils/tokenManager";

const RAG_API_URL =
  window.env?.REACT_APP_RAG_API_URL || "http://localhost:8002/api/v1";

const ragApi = axios.create({
  baseURL: RAG_API_URL,
});

tokenManager.setupInterceptors(ragApi);

export interface Blueprint {
  id: string;
  name: string;
  description: string;
  instructions: string;
  default_base_llm_config_id: string | null;
  default_planner_llm_config_id: string | null;
  default_mode: string;
  default_language: string;
  default_tools: string[] | null;
  default_temperature: number | null;
  default_max_tokens: number | null;
  default_document_ids: string[] | null;
  default_collection_ids: string[] | null;
  default_mcp_connection_ids?: string[] | null;
  default_mcp_tool_ids?: string[] | null;
  user_id: string;
  username: string;
  project_id: string | null;
  is_deleted: boolean;
  safety_status: "active" | "failed" | "pending" | string;
  created_at: string;
  updated_at: string;
  sharing: {
    scope: string;
  };
}

export interface BlueprintPaginationMetadata {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

export interface PaginatedBlueprintsResponse {
  items: Blueprint[];
  metadata?: BlueprintPaginationMetadata;
}

export interface ListBlueprintsParams {
  search?: string | null;
  page?: number;
  pageSize?: number;
  projectIds?: string[];
}

export interface BlueprintDocument {
  id: string;
  name?: string;
  filename?: string;
  file_name?: string;
  title?: string;
}

export interface CreateBlueprintPayload {
  name: string;
  project_id: string;
  description: string;
  instructions: string;
  default_base_llm_config_id: string | null;
  default_planner_llm_config_id?: string | null;
  default_mode: string;
  default_language: string;
  default_tools: string[] | null;
  // default_temperature: number | null;
  // default_max_tokens: number | null;
  default_document_ids: string[] | null;
  default_collection_ids: string[] | null;
  default_mcp_connection_ids: string[] | null;
  default_mcp_tool_ids: string[] | null;
  sharing: {
    scope: "inherit";
  };
}

const createBlueprintFormData = (
  payload: CreateBlueprintPayload,
  files: File[] = [],
) => {
  const formData = new FormData();
  formData.append(
    "blueprint_data",
    JSON.stringify(prepareBlueprintPayload(payload)),
  );
  files.forEach((file) => formData.append("files", file));
  return formData;
};

const prepareBlueprintPayload = (payload: CreateBlueprintPayload) => {
  const {
    default_planner_llm_config_id: plannerModelId,
    ...payloadWithoutPlanner
  } = payload;

  return payload.default_mode === "planner" && plannerModelId
    ? {
        ...payloadWithoutPlanner,
        default_planner_llm_config_id: plannerModelId,
      }
    : payloadWithoutPlanner;
};

const formatBlueprintApiErrorValue = (value: unknown): string | undefined => {
  if (typeof value === "string" && value.trim()) return value;
  if (Array.isArray(value)) {
    const messages = value
      .map((item) => formatBlueprintApiErrorValue(item))
      .filter(Boolean);
    return messages.length ? messages.join("; ") : undefined;
  }
  if (value && typeof value === "object") {
    const item = value as {
      msg?: unknown;
      message?: unknown;
      detail?: unknown;
      loc?: unknown;
    };
    const message =
      formatBlueprintApiErrorValue(item.msg) ||
      formatBlueprintApiErrorValue(item.message) ||
      formatBlueprintApiErrorValue(item.detail);
    if (message) {
      const location = Array.isArray(item.loc)
        ? item.loc.filter((part) => typeof part === "string").pop()
        : undefined;
      return location ? `${location}: ${message}` : message;
    }
    try {
      return JSON.stringify(value);
    } catch {
      return undefined;
    }
  }
  return undefined;
};

export const getBlueprintApiErrorMessage = (
  error: unknown,
  fallback: string,
) => {
  if (error && typeof error === "object") {
    const apiError = error as {
      message?: string;
      response?: { data?: unknown };
    };
    const responseData = apiError.response?.data;
    const responseMessage = formatBlueprintApiErrorValue(responseData);

    if (responseMessage) {
      if (responseData && typeof responseData === "object") {
        const data = responseData as {
          detail?: unknown;
          message?: unknown;
          error?: unknown;
        };
        return (
          formatBlueprintApiErrorValue(data.detail) ||
          formatBlueprintApiErrorValue(data.message) ||
          formatBlueprintApiErrorValue(data.error) ||
          responseMessage
        );
      }
      return responseMessage;
    }

    return apiError.message || fallback;
  }

  return fallback;
};

export const blueprintsApi = {
  listBlueprints: async ({
    search = null,
    page = 0,
    pageSize = 20,
    projectIds = [],
  }: ListBlueprintsParams = {}): Promise<PaginatedBlueprintsResponse> => {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    params.append("page", String(page));
    params.append("page_size", String(pageSize));
    for (const projectId of projectIds) {
      params.append("project_ids", projectId);
    }

    const response = await ragApi.get<
      Blueprint[] | PaginatedBlueprintsResponse
    >("/blueprints/", {
      params,
    });

    if (Array.isArray(response.data)) {
      return {
        items: response.data,
        metadata: {
          page,
          page_size: pageSize,
          total_items: response.data.length,
          total_pages: response.data.length ? 1 : 0,
        },
      };
    }

    return response.data;
  },

  getBlueprint: async (id: string): Promise<Blueprint> => {
    const response = await ragApi.get<Blueprint>(`/blueprints/${id}`);
    return response.data;
  },

  createBlueprint: async (
    payload: CreateBlueprintPayload,
    files: File[] = [],
  ): Promise<Blueprint> => {
    const response = await ragApi.post<Blueprint>(
      "/blueprints/",
      createBlueprintFormData(payload, files),
    );
    return response.data;
  },

  updateBlueprint: async (
    id: string,
    payload: CreateBlueprintPayload,
  ): Promise<Blueprint> => {
    const response = await ragApi.patch<Blueprint>(
      `/blueprints/${id}`,
      prepareBlueprintPayload(payload),
    );
    return response.data;
  },

  deleteBlueprint: async (id: string): Promise<void> => {
    await ragApi.delete(`/blueprints/${id}`, {
      params: { hard_delete: false },
    });
  },

  getBlueprintDocuments: async (
    blueprintId: string,
    { skip = 0, limit = 50 }: { skip?: number; limit?: number } = {},
  ): Promise<BlueprintDocument[]> => {
    const response = await ragApi.get<
      BlueprintDocument[] | { items?: BlueprintDocument[] }
    >(`/blueprints/${blueprintId}/documents`, {
      params: { skip, limit },
    });

    return Array.isArray(response.data)
      ? response.data
      : response.data.items || [];
  },

  deleteBlueprintDocument: async (
    blueprintId: string,
    documentId: string,
  ): Promise<void> => {
    await ragApi.delete(`/blueprints/${blueprintId}/documents/${documentId}`);
  },

  uploadBlueprintDocument: async (blueprintId: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await ragApi.post(
      `/blueprints/${blueprintId}/documents`,
      formData,
    );
    return response.data;
  },
};
